/**
 * useGameEvents Hook
 * Subscribes to game events from AppSync and provides callback registration
 */

import { useEffect, useRef } from 'react';
import { AppSyncChatClient } from '../utils/AppSyncChatClient';
import {
  GameSpinStartEvent,
  GameSpinEndEvent,
  GameWinEvent,
  GameLoseEvent,
  GameWinData,
} from '../types/betting.types';

interface UseGameEventsProps {
  chatClient: AppSyncChatClient;
}

interface UseGameEventsReturn {
  onGameSpinStart: (callback: (event: GameSpinStartEvent) => void) => void;
  onGameSpinEnd: (callback: (event: GameSpinEndEvent) => void) => void;
  onGameWin: (callback: (data: GameWinData) => void) => void;
  onGameLose: (callback: (event: GameLoseEvent) => void) => void;
}

/**
 * Custom hook to subscribe to game events from AppSync
 * Filters AppSync messages for game event types and provides callback registration
 */
export const useGameEvents = ({ chatClient }: UseGameEventsProps): UseGameEventsReturn => {
  // Store callbacks in refs to avoid re-registering message handlers
  const spinStartCallbacks = useRef<Array<(event: GameSpinStartEvent) => void>>([]);
  const spinEndCallbacks = useRef<Array<(event: GameSpinEndEvent) => void>>([]);
  const winCallbacks = useRef<Array<(data: GameWinData) => void>>([]);
  const loseCallbacks = useRef<Array<(event: GameLoseEvent) => void>>([]);

  useEffect(() => {
    // Register a single message handler with the chat client
    const handleMessage = (message: any) => {
      try {
        // Validate that message has an action field
        if (!message || typeof message.action !== 'string') {
          return;
        }

        // Filter and handle game events
        switch (message.action) {
          case 'GAME_SPIN_START':
            if (isValidGameSpinStartEvent(message)) {
              spinStartCallbacks.current.forEach((callback) => {
                try {
                  callback(message as GameSpinStartEvent);
                } catch (error) {
                  console.error('Error in GAME_SPIN_START callback:', error);
                }
              });
            } else {
              console.warn('Received malformed GAME_SPIN_START event, ignoring:', message);
            }
            break;

          case 'GAME_SPIN_END':
            if (isValidGameSpinEndEvent(message)) {
              spinEndCallbacks.current.forEach((callback) => {
                try {
                  callback(message as GameSpinEndEvent);
                } catch (error) {
                  console.error('Error in GAME_SPIN_END callback:', error);
                }
              });
            } else {
              console.warn('Received malformed GAME_SPIN_END event, ignoring:', message);
            }
            break;

          case 'GAME_WIN':
            if (isValidGameWinEvent(message)) {
              const winEvent = message as GameWinEvent;
              const winData: GameWinData = {
                winAmount: winEvent.message.winAmount,
                payMultiplier: winEvent.message.payMultiplier,
                winCoins: winEvent.message.winCoins,
                jackpotWin: winEvent.message.jackpotWin,
                winLines: winEvent.message.winLines,
                jackpotType: winEvent.message.jackpotType,
                totalBet: winEvent.message.totalBet,
                lineBet: winEvent.message.lineBet,
              };
              winCallbacks.current.forEach((callback) => {
                try {
                  callback(winData);
                } catch (error) {
                  console.error('Error in GAME_WIN callback:', error);
                }
              });
            } else {
              console.warn('Received malformed GAME_WIN event, ignoring:', message);
            }
            break;

          case 'GAME_LOSE':
            if (isValidGameLoseEvent(message)) {
              loseCallbacks.current.forEach((callback) => {
                try {
                  callback(message as GameLoseEvent);
                } catch (error) {
                  console.error('Error in GAME_LOSE callback:', error);
                }
              });
            } else {
              console.warn('Received malformed GAME_LOSE event, ignoring:', message);
            }
            break;

          default:
            // Not a game event, ignore
            break;
        }
      } catch (error) {
        console.error('Error handling game event:', error);
        // Continue processing other messages - don't crash
      }
    };

    // Register the message handler with the chat client
    chatClient.onMessage(handleMessage);

    // Cleanup: No explicit cleanup needed as the chat client manages handlers
    // and will be cleaned up when the component unmounts
  }, [chatClient]);

  // Callback registration functions
  // These replace any existing callbacks instead of accumulating them
  const onGameSpinStart = (callback: (event: GameSpinStartEvent) => void) => {
    spinStartCallbacks.current = [callback];
  };

  const onGameSpinEnd = (callback: (event: GameSpinEndEvent) => void) => {
    spinEndCallbacks.current = [callback];
  };

  const onGameWin = (callback: (data: GameWinData) => void) => {
    winCallbacks.current = [callback];
  };

  const onGameLose = (callback: (event: GameLoseEvent) => void) => {
    loseCallbacks.current = [callback];
  };

  return {
    onGameSpinStart,
    onGameSpinEnd,
    onGameWin,
    onGameLose,
  };
};

// Validation functions for event data

function isValidGameSpinStartEvent(event: any): boolean {
  return (
    event &&
    event.action === 'GAME_SPIN_START' &&
    event.message &&
    typeof event.message === 'object' &&
    typeof event.message.spinCount === 'number' &&
    typeof event.message.totalBet === 'number' &&
    typeof event.message.lineBet === 'number' &&
    typeof event.message.selectedLines === 'number' &&
    typeof event.message.isFreeSpin === 'boolean'
  );
}

function isValidGameSpinEndEvent(event: any): boolean {
  return (
    event &&
    event.action === 'GAME_SPIN_END' &&
    event.message &&
    typeof event.message === 'object'
  );
}

function isValidGameWinEvent(event: any): boolean {
  return (
    event &&
    event.action === 'GAME_WIN' &&
    event.message &&
    typeof event.message === 'object' &&
    typeof event.message.winAmount === 'number' &&
    typeof event.message.winCoins === 'number' &&
    typeof event.message.jackpotWin === 'number' &&
    typeof event.message.winLines === 'number' &&
    typeof event.message.jackpotType === 'string' &&
    typeof event.message.totalBet === 'number' &&
    typeof event.message.lineBet === 'number' &&
    typeof event.message.payMultiplier === 'number'
  );
}

function isValidGameLoseEvent(event: any): boolean {
  return (
    event &&
    event.action === 'GAME_LOSE' &&
    event.message &&
    typeof event.message === 'object' &&
    typeof event.message.totalBet === 'number' &&
    typeof event.message.lineBet === 'number'
  );
}
